// @greenlight-spec: 28-it-dashboard-ui.md
import * as React from 'react';

import { GLDrawer } from '@/components/greenlight/GLDrawer';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import type { FeedbackItem } from '@greenlight/contract';
import { extraHandoffLabel, statusVariant } from './presenter';

export interface FeedbackDetailDrawerProps {
  /** The feedback id to show, or null when the drawer is closed. */
  feedbackId: string | null;
  onClose: () => void;
}

function ContextFields({ item }: { item: FeedbackItem }): React.JSX.Element | null {
  const entries = Object.entries(item.context).filter(([, v]) => v !== null && v !== undefined);
  if (entries.length === 0) return null;
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-[12px]">
      {entries.map(([key, value]) => (
        <React.Fragment key={key}>
          <dt className="font-mono text-muted-foreground">{key}</dt>
          <dd className="break-words">{Array.isArray(value) ? value.join(', ') : String(value)}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

/**
 * The feedback detail panel (docs/28 §/feedback): envelope fields plus the body.
 * The body is untrusted input, so it renders as plain text inside a `<pre>` — no
 * Markdown/HTML parsing, no remote fetches — matching how the Knowledge review
 * surface renders agent-authored prose. React escapes the text content, so any
 * `<script>`/HTML in a body appears verbatim and never executes (AC-FEEDBACK-04).
 */
export function FeedbackDetailDrawer({
  feedbackId,
  onClose,
}: FeedbackDetailDrawerProps): React.JSX.Element {
  const detailKey = ['feedback-detail', feedbackId ?? ''];
  const query = api.feedback.get.useQuery(
    detailKey,
    { params: { id: feedbackId ?? '' } },
    { queryKey: detailKey, enabled: feedbackId !== null },
  );
  const item = query.data?.status === 200 ? query.data.body : null;

  const title = (
    <div className="min-w-0">
      <span className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground">
        Feedback · {item?.source ?? '…'}
      </span>
      <p className="mt-0.5 truncate text-sm font-semibold text-foreground">{item?.title ?? ' '}</p>
    </div>
  );

  return (
    <GLDrawer
      open={feedbackId !== null}
      onOpenChange={(o) => !o && onClose()}
      variant="panel"
      defaultWidth={640}
      minWidth={360}
      storageKey="gl-feedback-detail-width"
      title={title}
      srDescription="Feedback detail"
      contentProps={{ 'data-testid': 'feedback-detail' }}
    >
      {query.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : query.isError || !item ? (
        <Alert variant="destructive">
          <AlertDescription>Couldn&apos;t load this feedback. Retry in a moment.</AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge variant={statusVariant(item.status)}>{item.status}</StatusBadge>
            {extraHandoffLabel(item.status, item.sent_at) && (
              <StatusBadge variant="inactive">sent</StatusBadge>
            )}
            <span className="font-mono text-[11px] text-muted-foreground">{item.category}</span>
            {item.app_slug && (
              <span className="font-mono text-[11px] text-muted-foreground">{item.app_slug}</span>
            )}
          </div>
          <div className="text-[12px] text-muted-foreground">
            {item.submitted_by_email ?? item.submitted_by} · {item.created_at}
          </div>
          <ContextFields item={item} />
          <div>
            <div className="mb-1 font-mono text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground">
              Body
            </div>
            <pre
              data-testid="feedback-body"
              className="whitespace-pre-wrap break-words rounded-gl border border-border bg-muted/30 p-3 font-mono text-xs"
            >
              {item.body}
            </pre>
          </div>
        </div>
      )}
    </GLDrawer>
  );
}
